import React, { useState, useEffect } from "react";
import {
  AreaChart, Area,
  CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Label
} from "recharts";
import "./PnlHistoryChart.scss";
import { setLoadingFullAction } from '../../store/loading/loadingActions';
import { connect } from 'react-redux';
import { 
  TrendingUp, 
  ArrowUpCircle, 
  ArrowDownCircle,
  Clock,
  Activity,
  BarChart3,
} from 'lucide-react';

function PnlHistoryChart(props) {
  const {
    pnlHistoryData,
    setLoadingFull,
    onTotalPnlChange
  } = props;

  const [loading, setLoading] = useState(true);
  const [totalPnl, setTotalPnl] = useState(0);
  const [totalPnlPercent, setTotalPnlPercent] = useState(0);

  useEffect(() => {
    if (!pnlHistoryData || !pnlHistoryData.pnl_list) {
      setLoading(true);
      setLoadingFull({ isLoading: true, loadingText: 'Loading User Data...' });
      setTimeout(() => {
        setLoadingFull({ isLoading: false, loadingText: '' });
      }, 10000);
    } else {
      setLoading(false);
      setLoadingFull({ isLoading: false, loadingText: '' });

      const totalPnlValue = pnlHistoryData.pnl_list.reduce(
        (acc, item) => acc + item.pnl, 0);
      const totalPnlPercentValue = pnlHistoryData.pnl_list.reduce(
        (acc, item) => acc + item.pnl_percent, 0);

      setTotalPnl(totalPnlValue);
      setTotalPnlPercent(totalPnlPercentValue);
      if (typeof onTotalPnlChange === 'function') {
        onTotalPnlChange({ totalPnl: totalPnlValue, totalPnlPercent: totalPnlPercentValue });
      }
    }
  }, [pnlHistoryData, setLoadingFull, onTotalPnlChange]);

  const gradientOffset = () => {
    if (!pnlHistoryData || !pnlHistoryData.pnl_list || pnlHistoryData.pnl_list.length === 0) {
      return 0;
    }

    const dataMax = Math.max(...pnlHistoryData.pnl_list.map((i) => i.pnl));
    const dataMin = Math.min(...pnlHistoryData.pnl_list.map((i) => i.pnl));

    if (dataMax <= 0) return 0;
    if (dataMin >= 0) return 1;
    return dataMax / (dataMax - dataMin);
  };

  const off = gradientOffset();
  const isPositive = totalPnl >= 0;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const [date, time] = data.time.split(' ');
      
      return (
        <div className="modern-pnl-tooltip">
          <div className="tooltip-header">
            <Clock size={14} />
            <span>{date} {time}</span>
          </div>
          <div className="tooltip-content">
            <div className={`tooltip-value ${data.pnl >= 0 ? 'positive' : 'negative'}`}>
              ${data.pnl.toFixed(2)}
            </div>
            <div 
            className={`tooltip-percentage ${data.pnl_percent >= 0 ? 'positive' : 'negative'}`}>
              {data.pnl_percent >= 0 ? '+' : ''}{data.pnl_percent.toFixed(2)}%
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="modern-pnl-history-chart">
        <div className="chart-header-section">
          <div className="header-content">
            <div className="header-left">
              <div className="chart-title">
                <TrendingUp size={24} className="title-icon" />
                <h2>24h P&L Performance</h2>
              </div>
              <p className="chart-subtitle">Hourly profit & loss tracking</p>
            </div>
          </div>
        </div>

        <div className="modern-chart-container">
          <div className="chart-loading">
            <div className="loading-spinner">
              <TrendingUp size={48} className="spinner-icon" />
            </div>
            <p className="loading-text">Loading P&L data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!pnlHistoryData || !pnlHistoryData.pnl_list || pnlHistoryData.pnl_list.length === 0) {
    return (
      <div className="modern-pnl-history-chart">
        <div className="chart-header-section">
          <div className="header-content">
            <div className="header-left">
              <div className="chart-title">
                <TrendingUp size={24} className="title-icon" />
                <h2>24h P&L Performance</h2>
              </div>
              <p className="chart-subtitle">Hourly profit & loss tracking</p>
            </div>
          </div>
        </div>

        <div className="modern-chart-container">
          <div className="chart-empty-state">
            <BarChart3 size={64} className="empty-icon" />
            <h3>No Trading Activity</h3>
            <p>Your account has no transactions in the last 24 hours</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-pnl-history-chart">
      {/* Modern Header */}
      <div className="chart-header-section">
        <div className="header-content">
          <div className="header-left">
            <div className="chart-title">
              <Activity size={24} className="title-icon" />
              <h2>24h P&L Performance</h2>
            </div>
            <p className="chart-subtitle">Hourly profit & loss tracking</p>
            <span className="control-label">
              <Clock size={16} />
              Time Range:
            </span>
            <span className="info-value m-1">Last 24 Hours</span>
          </div>
          
          <div className="header-stats">
            <div className="stat-card">
              <div className="stat-icon">
                {isPositive ? 
                  <ArrowUpCircle size={24} className="profit-icon" /> : 
                  <ArrowDownCircle size={24} className="loss-icon" />
                }
              </div>
              <div className="stat-content">
                <span className="stat-label">Today's P&L</span>
                <div className="stat-values">
                  <span className={`stat-value ${isPositive ? 'positive' : 'negative'}`}>
                    <div>${totalPnl.toFixed(2)}</div>
                  </span>
                  <span className={`stat-percentage ${isPositive ? 'positive' : 'negative'}`}>
                    ({totalPnlPercent >= 0 ? '+' : ''}{totalPnlPercent.toFixed(2)}%)
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chart Info */}
      {/* <div className="chart-controls">
        <div className="controls-left">
          <div className="chart-info">
            <span className="control-label">
              <Clock size={16} />
              Time Range:
            </span>
            <span className="info-value">Last 24 Hours</span>
          </div>
        </div>
        
        <div className="controls-right">
          <div className="chart-info">
            <span className="control-label">
              <Activity size={16} />
              Data Points:
            </span>
            <span className="data-count">
              {pnlHistoryData.pnl_list.length} Hours
            </span>
          </div>
        </div>
      </div> */}

      {/* Modern Chart Container */}
      <div className="modern-chart-container">
        <div className="chart-wrapper">
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart
              data={pnlHistoryData?.pnl_list || []}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
              <XAxis
                dataKey="time"
                tick={({ x, y, payload }) => {
                  const [date, time] = payload.value.split(' ');
                  return (
                    <g transform={`translate(${x},${y + 10})`}>
                      <text x={0} y={0} dy={16} textAnchor="middle" fill="#6b7280" fontSize="12">
                        <tspan x="0" dy="0">{date}</tspan>
                        <tspan x="0" dy="15">{time}</tspan>
                      </text>
                    </g>
                  );
                }}
              >
                <Label
                  value="Hourly Timeline"
                  position="insideBottom"
                  offset={-40}
                  style={{ textAnchor: 'middle', fontWeight: '600', 
                  fill: '#374151', fontSize: '14px' }}
                />
              </XAxis>
              <YAxis dataKey="pnl" tick={{ fontSize: 12, fill: '#6b7280' }}>
                <Label
                  value="Profit & Loss ($)"
                  angle={-90}
                  offset={-5}
                  position="insideLeft"
                  style={{ textAnchor: 'middle', fontWeight: '600',
                   fill: '#374151', fontSize: '14px' }}
                />
              </YAxis>
              <Tooltip content={<CustomTooltip />} />
              <defs>
                <linearGradient id="modernPnlSplitColor" x1="0" y1="0" x2="0" y2="1">
                  <stop offset={off} stopColor="#10b981" stopOpacity={0.8} />
                  <stop offset={off} stopColor="#ef4444" stopOpacity={0.8} />
                </linearGradient>
                <linearGradient id="modernPnlAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="url(#modernPnlSplitColor)" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="url(#modernPnlSplitColor)" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="pnl"
                stroke="url(#modernPnlSplitColor)"
                fill="url(#modernPnlAreaGradient)"
                strokeWidth={3}
                activeDot={{ 
                  r: 8, 
                  strokeWidth: 3, 
                  stroke: '#fff',
                  fill: isPositive ? '#10b981' : '#ef4444',
                  filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.15))'
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    pnlHistoryData: state.botReducer.pnlHistoryData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setLoadingFull: (payload) => {
      dispatch(setLoadingFullAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PnlHistoryChart);